import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { 
  Truck, ChefHat, Package, Plus, Loader2, Calendar, 
  Users, Euro, Printer, Check, X, Clock, Building2,
  ShoppingCart, FileText, AlertCircle, Minus
} from 'lucide-react';
import { toast } from 'sonner';

const ORDER_STATUS = {
  pending: { label: 'In Attesa', color: 'bg-yellow-100 text-yellow-700' },
  confirmed: { label: 'Confermato', color: 'bg-blue-100 text-blue-700' },
  preparing: { label: 'In Preparazione', color: 'bg-purple-100 text-purple-700' },
  delivered: { label: 'Consegnato', color: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Cancellato', color: 'bg-red-100 text-red-700' }
};

const MEAL_TYPES = [
  { value: 'pranzo', label: 'Pranzo' },
  { value: 'cena', label: 'Cena' }
];

export const CateringPage = () => {
  const { api, user, structureId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('orders');
  
  // Structure info
  const [structureInfo, setStructureInfo] = useState(null);
  const isCatering = structureInfo?.production_type === 'catering' || structureInfo?.production_type === 'both';
  const canOrderCatering = structureInfo?.production_type === 'own' || structureInfo?.production_type === 'both';
  
  // Relations
  const [cateringProviders, setCateringProviders] = useState([]);  // Chi mi fornisce catering
  const [cateringClients, setCateringClients] = useState([]);  // Chi servo come catering
  
  // Orders
  const [myOrders, setMyOrders] = useState([]);  // Ordini che ho fatto
  const [receivedOrders, setReceivedOrders] = useState([]);  // Ordini ricevuti come catering
  
  // Order dialog
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [providerMenu, setProviderMenu] = useState([]);
  const [orderForm, setOrderForm] = useState({
    delivery_date: new Date().toISOString().split('T')[0],
    meal_type: 'pranzo',
    items: [],
    notes: ''
  });
  
  // Dashboard (for catering)
  const [dashboardDate, setDashboardDate] = useState(new Date().toISOString().split('T')[0]);
  const [dashboardData, setDashboardData] = useState(null);

  // Fetch structure info
  const fetchStructureInfo = useCallback(async () => {
    try {
      const res = await api.get(`/structures/${structureId}`);
      setStructureInfo(res.data);
    } catch (error) {
      console.error('Error fetching structure:', error);
    }
  }, [api, structureId]);

  // Fetch catering relations
  const fetchRelations = useCallback(async () => {
    try {
      // Who provides catering to us
      const providersRes = await api.get('/catering/relations?as_catering=false');
      setCateringProviders(providersRes.data);
      
      // Who we serve as catering
      const clientsRes = await api.get('/catering/relations?as_catering=true');
      setCateringClients(clientsRes.data);
    } catch (error) {
      console.error('Error fetching relations:', error);
    }
  }, [api]);

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    try {
      // My orders to catering providers
      const myRes = await api.get('/catering/orders?as_catering=false');
      setMyOrders(myRes.data);
      
      // Orders received as catering
      if (isCatering) {
        const receivedRes = await api.get('/catering/orders?as_catering=true');
        setReceivedOrders(receivedRes.data);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  }, [api, isCatering]);

  // Fetch dashboard
  const fetchDashboard = useCallback(async () => {
    if (!isCatering) return;
    try {
      const res = await api.get(`/catering/dashboard?date=${dashboardDate}`);
      setDashboardData(res.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  }, [api, isCatering, dashboardDate]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchStructureInfo();
    };
    loadData();
  }, [fetchStructureInfo]);

  useEffect(() => {
    if (structureInfo) {
      Promise.all([fetchRelations(), fetchOrders()]).then(() => setLoading(false));
    }
  }, [structureInfo, fetchRelations, fetchOrders]);

  useEffect(() => {
    if (isCatering) {
      fetchDashboard();
    }
  }, [isCatering, fetchDashboard]);

  // Open order dialog and load provider menu
  const openOrderDialog = async (provider) => {
    setSelectedProvider(provider);
    try {
      const res = await api.get(`/catering/menu?catering_structure_id=${provider.catering_structure_id}`);
      setProviderMenu(res.data);
      setOrderForm({
        delivery_date: new Date().toISOString().split('T')[0],
        meal_type: 'pranzo',
        items: [],
        notes: ''
      });
      setOrderDialogOpen(true);
    } catch (error) {
      toast.error('Errore nel caricamento del menu');
    }
  };

  // Add/remove item from order
  const updateOrderItem = (recipe, delta) => {
    setOrderForm(prev => {
      const items = [...prev.items];
      const existingIdx = items.findIndex(i => i.recipe_id === recipe.id);
      
      if (existingIdx >= 0) {
        items[existingIdx].quantity += delta;
        if (items[existingIdx].quantity <= 0) {
          items.splice(existingIdx, 1);
        }
      } else if (delta > 0) {
        items.push({
          recipe_id: recipe.id,
          recipe_name: recipe.name,
          quantity: delta,
          unit_price: recipe.food_cost_per_portion
        });
      }
      
      return { ...prev, items };
    });
  };

  const getOrderItemQuantity = (recipeId) => {
    const item = orderForm.items.find(i => i.recipe_id === recipeId);
    return item?.quantity || 0;
  };

  // Submit order
  const handleSubmitOrder = async () => {
    if (orderForm.items.length === 0) {
      toast.error('Aggiungi almeno una pietanza');
      return;
    }
    
    try {
      await api.post('/catering/orders', {
        catering_structure_id: selectedProvider.catering_structure_id,
        delivery_date: orderForm.delivery_date,
        meal_type: orderForm.meal_type,
        items: orderForm.items,
        notes: orderForm.notes
      });
      toast.success('Ordine inviato!');
      setOrderDialogOpen(false);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio dell\'ordine');
    }
  };

  // Update order status (catering only)
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await api.put(`/catering/orders/${orderId}`, { status: newStatus });
      toast.success('Stato aggiornato');
      fetchOrders();
      fetchDashboard();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  // Cancel order
  const cancelOrder = async (orderId) => {
    if (!window.confirm('Cancellare questo ordine?')) return;
    try {
      await api.delete(`/catering/orders/${orderId}`);
      toast.success('Ordine cancellato');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella cancellazione');
    }
  };

  // Print preparation sheet
  const printPreparationSheet = () => {
    if (!dashboardData) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Foglio Preparazione - ${dashboardData.date}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { font-size: 18px; border-bottom: 2px solid #000; padding-bottom: 10px; }
          h2 { font-size: 14px; margin-top: 20px; background: #f0f0f0; padding: 8px; }
          .summary { margin-bottom: 20px; }
          .recipe { margin: 15px 0; padding: 10px; border: 1px solid #ddd; }
          .recipe-name { font-size: 16px; font-weight: bold; }
          .total-portions { font-size: 24px; font-weight: bold; color: #2563eb; }
          .orders-list { font-size: 12px; margin-top: 10px; }
          .order-item { padding: 3px 0; border-bottom: 1px dotted #ccc; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>FOGLIO PREPARAZIONE CATERING</h1>
        <div class="summary">
          <p><strong>Data Consegna:</strong> ${dashboardData.date}</p>
          <p><strong>Totale Ordini:</strong> ${dashboardData.total_orders}</p>
          <p><strong>Strutture Servite:</strong> ${dashboardData.clients.join(', ')}</p>
          <p><strong>Totale Porzioni:</strong> <span class="total-portions">${dashboardData.total_portions}</span></p>
        </div>
        
        <h2>PIETANZE DA PREPARARE</h2>
        ${dashboardData.recipes.map(r => `
          <div class="recipe">
            <div class="recipe-name">${r.recipe_name}</div>
            <div class="total-portions">${r.total_portions} porzioni</div>
            <div class="orders-list">
              ${r.orders.map(o => `
                <div class="order-item">
                  ${o.satellite_name}: ${o.quantity} porzioni
                  ${o.notes ? `(${o.notes})` : ''}
                </div>
              `).join('')}
            </div>
          </div>
        `).join('')}
        
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20" data-testid="catering-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center text-white shadow-lg">
            <Truck className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Catering</h1>
            <p className="text-slate-500 text-sm">
              {isCatering && canOrderCatering ? 'Gestisci ordini e produzione catering' :
               isCatering ? 'Gestisci la produzione per le strutture servite' :
               'Ordina dalle strutture catering'}
            </p>
          </div>
        </div>
        
        {/* Structure type badge */}
        <Badge className={
          structureInfo?.production_type === 'both' ? 'bg-purple-100 text-purple-700' :
          isCatering ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
        }>
          {structureInfo?.production_type === 'both' ? 'Produzione + Catering' :
           isCatering ? 'Struttura Catering' : 'Produzione Propria'}
        </Badge>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-lg grid-cols-3">
          {canOrderCatering && (
            <TabsTrigger value="orders" className="flex items-center gap-2" data-testid="orders-tab">
              <ShoppingCart className="w-4 h-4" />
              I Miei Ordini
            </TabsTrigger>
          )}
          {isCatering && (
            <>
              <TabsTrigger value="received" className="flex items-center gap-2" data-testid="received-tab">
                <Package className="w-4 h-4" />
                Ordini Ricevuti
              </TabsTrigger>
              <TabsTrigger value="dashboard" className="flex items-center gap-2" data-testid="dashboard-tab">
                <ChefHat className="w-4 h-4" />
                Preparazione
              </TabsTrigger>
            </>
          )}
        </TabsList>

        {/* My Orders Tab (Satellite view) */}
        {canOrderCatering && (
          <TabsContent value="orders" className="space-y-4">
            {/* Catering Providers */}
            {cateringProviders.length > 0 ? (
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-700">Fornitori Catering</h3>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {cateringProviders.map(provider => (
                    <Card key={provider.id} className="border-emerald-200">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-semibold">{provider.catering_structure_name}</p>
                          </div>
                        </div>
                        <Button
                          className="w-full bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => openOrderDialog(provider)}
                          data-testid={`order-from-${provider.id}`}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Nuovo Ordine
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* My Orders List */}
                <h3 className="font-semibold text-slate-700 mt-6">Storico Ordini</h3>
                {myOrders.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="py-8 text-center text-slate-500">
                      Nessun ordine effettuato
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {myOrders.map(order => (
                      <Card key={order.id} data-testid={`order-${order.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-slate-400" />
                              <span className="font-medium">{order.delivery_date}</span>
                              <Badge variant="outline">{order.meal_type}</Badge>
                            </div>
                            <Badge className={ORDER_STATUS[order.status]?.color}>
                              {ORDER_STATUS[order.status]?.label}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600 mb-2">
                            Da: <strong>{order.catering_structure_name}</strong>
                          </p>
                          <div className="flex justify-between items-center text-sm">
                            <span>{order.total_portions} porzioni</span>
                            {order.status === 'pending' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600"
                                onClick={() => cancelOrder(order.id)}
                              >
                                <X className="w-4 h-4 mr-1" />
                                Annulla
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <Truck className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">Nessun fornitore catering collegato</p>
                  <p className="text-slate-400 text-sm mt-1">
                    Contatta l'amministratore per abilitare un servizio catering
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        )}

        {/* Received Orders Tab (Catering view) */}
        {isCatering && (
          <TabsContent value="received" className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-700">
                Ordini da Preparare ({receivedOrders.filter(o => o.status !== 'cancelled').length})
              </h3>
            </div>
            
            {receivedOrders.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <Package className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">Nessun ordine ricevuto</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {receivedOrders.map(order => (
                  <Card key={order.id} className={order.status === 'cancelled' ? 'opacity-50' : ''}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          <span className="font-medium">{order.delivery_date}</span>
                          <Badge variant="outline">{order.meal_type}</Badge>
                        </div>
                        <Badge className={ORDER_STATUS[order.status]?.color}>
                          {ORDER_STATUS[order.status]?.label}
                        </Badge>
                      </div>
                      
                      <p className="text-sm text-slate-600 mb-3">
                        Da: <strong>{order.satellite_structure_name}</strong>
                      </p>
                      
                      {/* Items */}
                      <div className="bg-slate-50 rounded-lg p-3 mb-3">
                        {order.items.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm py-1">
                            <span>{item.recipe_name}</span>
                            <span className="font-medium">{item.quantity} porzioni</span>
                          </div>
                        ))}
                        <div className="border-t mt-2 pt-2 flex justify-between font-medium">
                          <span>Totale</span>
                          <span>{order.total_portions} porzioni</span>
                        </div>
                      </div>
                      
                      {/* Status actions */}
                      {order.status !== 'cancelled' && order.status !== 'delivered' && (
                        <div className="flex gap-2">
                          {order.status === 'pending' && (
                            <Button
                              size="sm"
                              className="flex-1 bg-blue-600 hover:bg-blue-700"
                              onClick={() => updateOrderStatus(order.id, 'confirmed')}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Conferma
                            </Button>
                          )}
                          {order.status === 'confirmed' && (
                            <Button
                              size="sm"
                              className="flex-1 bg-purple-600 hover:bg-purple-700"
                              onClick={() => updateOrderStatus(order.id, 'preparing')}
                            >
                              <ChefHat className="w-4 h-4 mr-1" />
                              In Preparazione
                            </Button>
                          )}
                          {order.status === 'preparing' && (
                            <Button
                              size="sm"
                              className="flex-1 bg-green-600 hover:bg-green-700"
                              onClick={() => updateOrderStatus(order.id, 'delivered')}
                            >
                              <Truck className="w-4 h-4 mr-1" />
                              Consegnato
                            </Button>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        )}

        {/* Dashboard Tab (Catering view) */}
        {isCatering && (
          <TabsContent value="dashboard" className="space-y-4">
            {/* Date selector */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <Input
                  type="date"
                  value={dashboardDate}
                  onChange={(e) => setDashboardDate(e.target.value)}
                  className="w-auto"
                />
              </div>
              <Button variant="outline" onClick={printPreparationSheet} disabled={!dashboardData?.recipes?.length}>
                <Printer className="w-4 h-4 mr-2" />
                Stampa Foglio
              </Button>
            </div>
            
            {dashboardData ? (
              <>
                {/* Summary cards */}
                <div className="grid gap-4 sm:grid-cols-3">
                  <Card>
                    <CardContent className="p-4 text-center">
                      <FileText className="w-8 h-8 mx-auto text-blue-500 mb-2" />
                      <p className="text-2xl font-bold">{dashboardData.total_orders}</p>
                      <p className="text-sm text-slate-500">Ordini</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <Building2 className="w-8 h-8 mx-auto text-purple-500 mb-2" />
                      <p className="text-2xl font-bold">{dashboardData.total_clients}</p>
                      <p className="text-sm text-slate-500">Strutture</p>
                    </CardContent>
                  </Card>
                  <Card className="border-emerald-200 bg-emerald-50">
                    <CardContent className="p-4 text-center">
                      <Package className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
                      <p className="text-3xl font-bold text-emerald-700">{dashboardData.total_portions}</p>
                      <p className="text-sm text-emerald-600">Porzioni Totali</p>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Recipes breakdown */}
                <h3 className="font-semibold text-slate-700">Dettaglio Pietanze</h3>
                {dashboardData.recipes.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="py-8 text-center text-slate-500">
                      Nessun ordine per questa data
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {dashboardData.recipes.map(recipe => (
                      <Card key={recipe.recipe_id}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-semibold text-lg">{recipe.recipe_name}</h4>
                            <Badge className="bg-emerald-100 text-emerald-700 text-lg px-3 py-1">
                              {recipe.total_portions} porzioni
                            </Badge>
                          </div>
                          <div className="space-y-1 text-sm">
                            {recipe.orders.map((order, idx) => (
                              <div key={idx} className="flex justify-between py-1 border-b last:border-0">
                                <span className="text-slate-600">{order.satellite_name}</span>
                                <span className="font-medium">{order.quantity} porz.</span>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <Loader2 className="w-8 h-8 mx-auto animate-spin text-slate-300 mb-4" />
                  <p className="text-slate-500">Caricamento dashboard...</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* Order Dialog */}
      <Dialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuovo Ordine Catering</DialogTitle>
            <DialogDescription>
              Ordina da {selectedProvider?.catering_structure_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Date and meal type */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data Consegna</Label>
                <Input
                  type="date"
                  value={orderForm.delivery_date}
                  onChange={(e) => setOrderForm(prev => ({ ...prev, delivery_date: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="space-y-2">
                <Label>Pasto</Label>
                <Select
                  value={orderForm.meal_type}
                  onValueChange={(v) => setOrderForm(prev => ({ ...prev, meal_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MEAL_TYPES.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Menu */}
            <div className="space-y-2">
              <Label>Seleziona Pietanze</Label>
              {providerMenu.length === 0 ? (
                <p className="text-sm text-slate-500">Nessuna ricetta disponibile</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {providerMenu.map(recipe => {
                    const qty = getOrderItemQuantity(recipe.id);
                    return (
                      <div 
                        key={recipe.id}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          qty > 0 ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200'
                        }`}
                      >
                        <div>
                          <p className="font-medium">{recipe.name}</p>
                          <p className="text-xs text-slate-500">
                            {recipe.category} • {recipe.portions_produced} porz/ricetta
                            {recipe.allergens?.length > 0 && (
                              <span className="text-red-500 ml-2">
                                Allergeni: {recipe.allergens.join(', ')}
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => updateOrderItem(recipe, -1)}
                            disabled={qty === 0}
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <span className="w-12 text-center font-medium">{qty}</span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => updateOrderItem(recipe, 1)}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Order summary */}
            {orderForm.items.length > 0 && (
              <div className="bg-emerald-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Riepilogo Ordine</h4>
                {orderForm.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm py-1">
                    <span>{item.recipe_name}</span>
                    <span>{item.quantity} porzioni</span>
                  </div>
                ))}
                <div className="border-t mt-2 pt-2 flex justify-between font-semibold">
                  <span>Totale</span>
                  <span>{orderForm.items.reduce((sum, i) => sum + i.quantity, 0)} porzioni</span>
                </div>
              </div>
            )}
            
            {/* Notes */}
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                value={orderForm.notes}
                onChange={(e) => setOrderForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Note aggiuntive per l'ordine..."
                rows={2}
              />
            </div>
            
            <Button
              onClick={handleSubmitOrder}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              disabled={orderForm.items.length === 0}
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              Invia Ordine
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
